import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "react-hot-toast";
import mentorshipService from "@/services/mentorshipService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function CSVUploadModal({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [courseUrl, setCourseUrl] = useState("https://classplusapp.com/diy/courses/course-overview?id=");
  const [batchPrefix, setBatchPrefix] = useState("A");
  const [numberOfBatches, setNumberOfBatches] = useState(1);
  const [batchNamingFormat, setBatchNamingFormat] = useState("alphabetical"); // "alphabetical" or "numerical"
  const [autoComputeBatches, setAutoComputeBatches] = useState(false);
  const [batchSize, setBatchSize] = useState(50);
  const [courseIds, setCourseIds] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [generatedBatchIds, setGeneratedBatchIds] = useState([]);

  // ✅ FIXED: Add proper cleanup when modal opens/closes
  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      handleReset();
      // Focus the first input when modal opens
      setTimeout(() => {
        const firstInput = document.querySelector('#file');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } else {
      // Cleanup when modal closes
      setIsUploading(false);
      setError(null);
      setProgress(0);
    }
  }, [open]);

  // ✅ FIXED: Add cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup function
      setIsUploading(false);
      setError(null);
      setProgress(0);
    };
  }, []);

  // Generate batch IDs based on prefix and number
  useEffect(() => {
    if (batchPrefix && numberOfBatches > 0) {
      let batches;
      
      if (batchNamingFormat === "alphabetical") {
        // Generate alphabetical sequence: A, B, C... Z, AA, AB, etc.
        const generateAlphabeticalSuffix = (index) => {
          // Convert to base-26 representation using alphabets
          let suffix = '';
          let num = index + 1; // +1 because we start with A (index 0)
          
          while (num > 0) {
            num--; // Adjust for 0-based index
            const remainder = num % 26;
            suffix = String.fromCharCode(65 + remainder) + suffix; // 65 is ASCII for 'A'
            num = Math.floor(num / 26);
          }
          
          return suffix || 'A'; // Default to 'A' if empty
        };
        
        batches = Array.from({ length: numberOfBatches }, (_, i) => 
          `${batchPrefix}-${generateAlphabeticalSuffix(i)}`
        );
      } else {
        // Generate numerical sequence: 1, 2, 3, etc.
        batches = Array.from({ length: numberOfBatches }, (_, i) => 
          `${batchPrefix}-${i + 1}`
        );
      }
      
      setGeneratedBatchIds(batches);
    } else {
      setGeneratedBatchIds([]);
    }
  }, [batchPrefix, numberOfBatches, batchNamingFormat]);

  // Validate course IDs to match number of batches
  const validateCourseIds = () => {
    if (!courseIds.trim()) return true; // Empty is valid
    
    const ids = courseIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    return ids.length === numberOfBatches;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
      setError("Please select a valid CSV file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    if (!courseName) {
      setError("Please enter the course name");
      return;
    }

    if (!courseUrl) {
      setError("Please enter the course URL");
      return;
    }

    if (!batchPrefix) {
      setError("Please enter the batch prefix");
      return;
    }
    
    if (numberOfBatches <= 0) {
      setError("Number of batches must be greater than zero");
      return;
    }
    
    if (courseIds && !validateCourseIds()) {
      setError("Number of course IDs must match number of batches");
      return;
    }

    const uploadToastId = toast.loading("Uploading CSV...");
    try {
      setIsUploading(true);
      setError(null);
      setProgress(10);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("courseName", courseName);
      formData.append("courseUrl", courseUrl);
      formData.append("batchIds", generatedBatchIds.join(','));
      formData.append("batchSize", batchSize.toString());
      formData.append("batchNamingFormat", batchNamingFormat);
      if (courseIds) {
        formData.append("courseIds", courseIds);
      }
      if (autoComputeBatches) {
        formData.append("autoComputeBatches", "true");
      }

      setProgress(30);
      const response = await mentorshipService.uploadCSV(formData);
      setProgress(100);

      toast.success("CSV uploaded successfully!", { id: uploadToastId });
      
      // Call onSuccess callback with success flag
      if (onSuccess) {
        onSuccess(response);
      }

      // Reset form
      handleReset();
      onOpenChange(false);
    } catch (err) {
      console.error("Error uploading CSV:", err);
      const errorMessage = err.response?.data?.error || "Failed to upload CSV. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, { id: uploadToastId });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setCourseName("");
    setCourseUrl("https://classplusapp.com/diy/courses/course-overview?id=");
    setBatchPrefix("A");
    setNumberOfBatches(1);
    setBatchNamingFormat("alphabetical");
    setAutoComputeBatches(false);
    setBatchSize(50);
    setCourseIds("");
    setError(null);
    setProgress(0);
    setGeneratedBatchIds([]);
  };

  // ✅ FIXED: Proper modal close handler
  const handleModalClose = (newOpen) => {
    if (!newOpen) {
      // Cleanup when modal is closing
      handleReset();
      setIsUploading(false);
      setError(null);
      setProgress(0);
    }
    onOpenChange(newOpen);
  };

  const handleNumberChange = (e, setter) => {
    setter(parseInt(e.target.value) || 1);
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with student data to add to a course.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="file" className="mb-1">CSV File*</Label>
            <Input
              id="file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <p className="text-xs text-muted-foreground">
              File should have columns: name, countryCode, mobile
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="courseName" className="mb-1">Course Name*</Label>
            <Input
              id="courseName"
              placeholder="Enter course name"
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              disabled={isUploading}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="courseUrl" className="mb-1">Course URL*</Label>
            <Input
              id="courseUrl"
              type="url"
              placeholder="https://classplusapp.com/diy/courses/course-overview?id="
              value={courseUrl}
              onChange={(e) => setCourseUrl(e.target.value)}
              disabled={isUploading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="batchPrefix" className="mb-1">Batch Prefix*</Label>
              <Input
                id="batchPrefix"
                placeholder="A, B, GS, GS1"
                value={batchPrefix}
                onChange={(e) => setBatchPrefix(e.target.value)}
                disabled={isUploading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="numberOfBatches" className="mb-1">Number of Batches*</Label>
              <Input
                id="numberOfBatches"
                type="text"
                placeholder="1"
                value={numberOfBatches}
                onChange={(e) => handleNumberChange(e, setNumberOfBatches)}
                disabled={isUploading}
              />
            </div>
          </div>

          <div className="grid gap-2 border-t border-b py-4 my-2">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="batchNamingFormat" className="text-md font-semibold">Batch Naming Format</Label>
                <p className="text-xs text-muted-foreground">
                  Choose how batches will be numbered
                </p>
              </div>
              <Select 
                value={batchNamingFormat} 
                onValueChange={(value) => setBatchNamingFormat(value)}
                disabled={isUploading}
              >
                <SelectTrigger id="batchNamingFormat" className="w-[240px]">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alphabetical">Alphabetical (Prefix-A, Prefix-B...)</SelectItem>
                  <SelectItem value="numerical">Numerical (Prefix-1, Prefix-2...)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-gray-50 p-2 rounded-md mt-2">
              <p className="text-sm">
                <span className="font-medium">Format: </span>
                {batchNamingFormat === "alphabetical" ? 
                  `${batchPrefix}-A, ${batchPrefix}-B, ${batchPrefix}-C, ... ${batchPrefix}-Z, ${batchPrefix}-AA, etc.` : 
                  `${batchPrefix}-1, ${batchPrefix}-2, ${batchPrefix}-3, etc.`}
              </p>
            </div>
          </div>

          {batchNamingFormat === "alphabetical" && numberOfBatches > 26 && (
            <p className="text-xs text-orange-500 font-semibold">
              Warning: Single-letter alphabetical batches are limited to 26 (A-Z). 
              Batches beyond 26 will use double letters (AA, AB, etc.)
            </p>
          )}

          {generatedBatchIds.length > 0 && (
            <div className="grid gap-2">
              <Label className="mb-1">Generated Batch IDs:</Label>
              <div className="bg-gray-100 p-2 rounded-md text-sm max-h-24 overflow-y-auto">
                {generatedBatchIds.join(', ')}
              </div>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="batchSize" className="mb-1">Batch Size</Label>
            <Input
              id="batchSize"
              type="number"
              min="1"
              value={batchSize}
              onChange={(e) => handleNumberChange(e, setBatchSize)}
              disabled={isUploading}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="courseIds" className="mb-1">Course IDs (comma-separated)</Label>
            <Input
              id="courseIds"
              placeholder="ID1,ID2,ID3"
              value={courseIds}
              onChange={(e) => setCourseIds(e.target.value)}
              disabled={isUploading}
            />
            <p className="text-xs text-muted-foreground">
              Each ID will be mapped to a batch in order (e.g., first ID → first batch). 
              Number of IDs should match number of batches ({numberOfBatches}).
            </p>
            {courseIds && !validateCourseIds() && (
              <p className="text-xs text-red-500">
                Warning: The number of course IDs ({courseIds.split(/[\n,]/).map(id => id.trim()).filter(id => id).length}) 
                does not match number of batches ({numberOfBatches}).
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="autoComputeBatches"
              checked={autoComputeBatches}
              onCheckedChange={setAutoComputeBatches}
              disabled={isUploading}
            />
            <Label htmlFor="autoComputeBatches">Automatically compute batch assignments</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isUploading}>
              {isUploading ? `Uploading... ${progress}%` : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

CSVUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default CSVUploadModal; 